import { useEffect, useState, useRef } from 'react';
import { fetchJournalPhotoObjectUrl } from '../utils/cloudApi';

/**
 * Renders a journal attachment: uses `url` when present (legacy signed GCS URL),
 * otherwise loads bytes via authenticated `/api/journal/photos/file` (no IAM signBlob on read).
 * Expired direct URLs and failed fetches clear `src` and call `onUnavailable` when provided.
 */
export function JournalAttachmentImage({ url, storagePath, alt = '', className = '', onUnavailable }) {
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;

  const notifyUnavailable = () => {
    onUnavailableRef.current?.();
  };

  const [src, setSrc] = useState(() => (String(url || '').trim() ? String(url).trim() : ''));

  useEffect(() => {
    let revoked = false;
    let objectUrl = '';

    const direct = String(url || '').trim();
    if (direct) {
      setSrc(direct);
      return () => {
        revoked = true;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }

    const path = String(storagePath || '').trim();
    if (!path) {
      setSrc('');
      return undefined;
    }

    setSrc('');
    (async () => {
      try {
        objectUrl = await fetchJournalPhotoObjectUrl(path);
        if (!revoked) setSrc(objectUrl);
      } catch {
        if (!revoked) {
          setSrc('');
          notifyUnavailable();
        }
      }
    })();

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, storagePath]);

  const handleImgError = () => {
    setSrc('');
    notifyUnavailable();
  };

  if (!src) return null;
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={handleImgError}
    />
  );
}
